import { create } from 'zustand';
import { getCachedIdToken } from '../../services/network';

// Apple subscription owned by this device's Apple ID that the SERVER has
// confirmed is live and linked to no account. appleIAP sets it when POST
// /subscription/apple-check answers state 'live', linked 'none' — with the
// server's expiresAt — about a transaction StoreKit re-delivered outside the
// user-initiated window (i.e. at launch), or about a purchase or restore whose
// apple-verify failed (1.4.3 left a marker there too, so a payer whose link
// failed still gets the Restore prompt). The client cannot judge
// "live" itself: CdvPurchase's Apple adapter never exposes an expiry, which is
// how 1.4.3 told an expired subscriber "a subscription is active on this
// device". Cleared once a verify links it, or once a verify says it ended.
// RestoreSubscriptionBanner turns it into a user-initiated Restore offer — the
// only canon-compliant way to link an unlinked receipt.
//
// Persisted: StoreKit 1 only replays UNFINISHED transactions at launch, so the
// out-of-window `approved` that detects an owned receipt happens once per
// renewal. Without persistence the marker would live for a single session.
//
// Never stored without an expiry that ages it out: a 1.4.3 marker (expiresAt
// null) is discarded on read, and a write without a future expiresAt clears
// the marker instead. MAX_AGE_MS caps it too (a yearly expiry is far away).
//
// Scoped to the Cognito user who was signed in when it was confirmed: on a
// shared device a banner shown to the WRONG account would invite them to claim
// someone else's subscription. A passive Restore Purchases button stays
// available to everyone; only this prompt is account-scoped.
const STORAGE_KEY = 'babytrack-iap-owned-receipt';
const MAX_AGE_MS = 45 * 86400000;

// Transactions still waiting for an apple-check answer (see the pending-check
// helpers below). Bounded like the fleet's durable verify queue
// (shared-docs/in-app-purchases.md, Consumables (b)): 6 tries, 7 days.
const PENDING_KEY = 'babytrack-iap-pending-check';
const PENDING_MAX_ATTEMPTS = 6;
const PENDING_MAX_AGE_MS = 7 * 86400000;
const PENDING_MAX_ENTRIES = 10;

// Cognito sub from the cached idToken — synchronous, so the store can filter on
// read. Null when signed out or the token is unreadable. Also the
// appAccountToken source for StoreKit (appleIAP's store.applicationUsername).
export function currentUserSub() {
  try {
    const token = getCachedIdToken();
    if (!token) return null;
    const payload = token.split('.')[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json).sub || null;
  } catch {
    return null;
  }
}

/** True for a marker with a product, a future server expiry and an age under MAX_AGE_MS. */
export function isMarkerLive(marker, now = Date.now()) {
  if (!marker || !marker.productId) return false;
  const expiresMs = Date.parse(marker.expiresAt || '');
  if (!Number.isFinite(expiresMs) || expiresMs <= now) return false;
  if (marker.seenAt && now - marker.seenAt > MAX_AGE_MS) return false;
  return true;
}

function readPersisted() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    if (!isMarkerLive(v) || !v.userSub) {
      // Useless to every account: ended, never had an expiry (1.4.3), or
      // stored while signed out. Drop it rather than re-read it each launch.
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    if (v.userSub !== currentUserSub()) return null; // another account's receipt
    return v;
  } catch {
    return null;
  }
}

function writePersisted(marker) {
  try {
    if (marker) localStorage.setItem(STORAGE_KEY, JSON.stringify(marker));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* localStorage unavailable — in-memory only */
  }
}

// The stored shape, or null when the marker could not age out or has no owner.
function toMarker(owned) {
  if (!owned) return null;
  const userSub = currentUserSub();
  if (!userSub) return null;
  const marker = {
    productId: owned.productId,
    transactionId: owned.transactionId || null,
    expiresAt: owned.expiresAt || null,
    userSub,
    seenAt: Date.now(),
  };
  return isMarkerLive(marker) ? marker : null;
}

// ── Pending apple-check questions ────────────────────────────────────────────
// StoreKit is told a purchase or restore is finished before apple-verify has
// answered, and StoreKit 1 never hands a finished transaction back. So when
// apple-verify fails and the read-only apple-check that should confirm the
// owned-receipt marker gets no answer either (offline, 5xx, timeout, or the
// server could not look the transaction up), the question is kept here and
// asked again on the next launch or sign-in (appleIAP retryPendingChecks).
// A definitive answer (live, expired or revoked) removes it; so do 6 tries or
// 7 days, so a transaction the server can never look up is not asked forever.
// Each entry belongs to the Cognito sub that was signed in when it was made
// and is only ever asked for that account.

function isPendingEntry(e) {
  return Boolean(
    e &&
      typeof e.transactionId === 'string' &&
      typeof e.productId === 'string' &&
      typeof e.userSub === 'string' &&
      Number.isFinite(e.attempts) &&
      Number.isFinite(e.firstSeenAt)
  );
}

function readPendingList() {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    const list = raw ? JSON.parse(raw) : [];
    return Array.isArray(list) ? list.filter(isPendingEntry) : [];
  } catch {
    return [];
  }
}

function writePendingList(list) {
  try {
    if (list.length) localStorage.setItem(PENDING_KEY, JSON.stringify(list));
    else localStorage.removeItem(PENDING_KEY);
  } catch {
    /* localStorage unavailable — the question is simply not asked again */
  }
}

// Newest first, without spent or stale entries.
function prunePending(list, now) {
  return list
    .filter((e) => e.attempts < PENDING_MAX_ATTEMPTS && now - e.firstSeenAt < PENDING_MAX_AGE_MS)
    .sort((a, b) => b.firstSeenAt - a.firstSeenAt)
    .slice(0, PENDING_MAX_ENTRIES);
}

/** This account's transactions still waiting for an apple-check answer: [{ transactionId, productId, userSub, attempts, firstSeenAt }]. */
export function readPendingChecks(userSub = currentUserSub()) {
  const list = readPendingList();
  const kept = prunePending(list, Date.now());
  if (kept.length !== list.length) writePendingList(kept);
  return userSub ? kept.filter((e) => e.userSub === userSub) : [];
}

/**
 * Keep a transaction apple-check gave no definitive answer for, or count
 * another try for one already kept. `countAttempt: false` keeps it without
 * using up a try (the device was offline, so the server was never asked).
 */
export function notePendingCheck({ transactionId, productId, userSub = currentUserSub() } = {}, { countAttempt = true } = {}) {
  if (!transactionId || !productId || !userSub) return;
  const now = Date.now();
  const list = readPendingList();
  const entry = list.find((e) => e.transactionId === transactionId && e.userSub === userSub);
  if (entry) {
    if (countAttempt) entry.attempts += 1;
  } else {
    list.push({ transactionId, productId, userSub, attempts: countAttempt ? 1 : 0, firstSeenAt: now });
  }
  writePendingList(prunePending(list, now));
}

/** Drop a transaction once apple-check has answered definitively for this account. */
export function forgetPendingCheck(transactionId, userSub = currentUserSub()) {
  const list = readPendingList();
  const kept = list.filter((e) => !(e.transactionId === transactionId && e.userSub === userSub));
  if (kept.length !== list.length) writePendingList(kept);
}

export const useIapStore = create((set) => ({
  ownedReceipt: readPersisted(),
  // Why the last apple-verify batch did not link: 'conflict' (409 — the
  // subscription belongs to a different BabyTrack account) or 'failed' (5xx,
  // timeout, offline). Null after a link, an "ended" answer, or a new restore.
  linkError: null,
  // The last classified verify batch: { outcome, at, productIds } with outcome
  // 'linked' | 'expired' | 'conflict' | 'failed'. The paywall reads it to tell
  // a completed purchase from a refused one.
  lastOutcome: null,
  setOwnedReceipt: (receipt) => {
    const marker = toMarker(receipt);
    writePersisted(marker);
    set({ ownedReceipt: marker });
  },
  setLinkError: (linkError) => set({ linkError }),
  setLastOutcome: (outcome, productIds = []) =>
    set({ lastOutcome: { outcome, at: Date.now(), productIds } }),
  // Dismissing clears the persisted marker too — otherwise an un-linkable
  // receipt (e.g. bought on another BabyTrack account) would re-show the
  // banner on every launch until it ages out.
  dismissNudge: () => {
    writePersisted(null);
    set({ ownedReceipt: null, linkError: null });
  },
  // Re-read for whoever is signed in now. Safe on every auth transition
  // (sign-in by any route, sign-up, sign-out): it never throws, and resets the
  // previous account's link error and verify outcome.
  refreshFromStorage: () => set({ ownedReceipt: readPersisted(), linkError: null, lastOutcome: null }),
}));
